import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { push } from "react-router-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";

import { actions as fundcorpActions } from "reducers/fundcorp";

import { Box, Button, ContextMenu } from "@ui-kit/components/index.es";
import DownloadButton from "pages/_components/DownloadButton";
import { isEnabledMvp } from "util/fundcorp";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import MonthlyReportsDrawer from "./MonthlyReportsDrawer";

function FundcorpRightContent(props) {
    const {
        activeEnvironment,
        isDesktop,
        dispatch,
        isSmallDesktop,
        isFetchingFundcorp,
        isFetchingDownload,
        hasPositionsToday,
        handleExport,
        shareAccounts,
        embeddedInV3Shell,
        location,
        hideDownload,
    } = props;

    let itemActiveMultiple = [];

    const showMvp4Release = isEnabledMvp("MVP4");
    const showMonthlyReports = configUtils.getBoolean("frontend.show.fundExtracts.functionalities", false);

    if (showMvp4Release) {
        itemActiveMultiple = [
            ...itemActiveMultiple,
            {
                icon: "UserList",
                iconVariant: "outline",
                label: i18n.get("fund.operation.investorProfile.menu.label"),
                callback: () => dispatch(push("/fundcorpOperations/investor-profile")),
            },
        ];
    }

    if (isDesktop && !hideDownload) {
        itemActiveMultiple = [
            ...itemActiveMultiple,
            {
                icon: "SendDown",
                iconVariant: "outline",
                label: i18n.get("global.download"),
                subItems: [
                    {
                        label: i18n.get("global.download.pdf"),
                        callback: () => handleExport("pdf"),
                    },
                    {
                        label: i18n.get("global.download.xls"),
                        callback: () => handleExport("xls"),
                    },
                    {
                        label: i18n.get("global.download.csv"),
                        callback: () => handleExport("csv"),
                    },
                ],
            },
        ];
    }

    const getReturnPath = () => {
        if (location?.pathname) {
            return `${location.pathname}${location.search || ""}`;
        }
        return undefined;
    };

    const handleSubscribe = (url) => {
        const returnPath = getReturnPath();
        if (showMvp4Release) {
            dispatch(fundcorpActions.getInvestorProfile(url, returnPath));
        } else {
            dispatch(push({ pathname: url, state: returnPath ? { return: returnPath } : undefined }));
        }
    };

    const handleRescue = (url) => {
        const returnPath = getReturnPath();
        dispatch(push({ pathname: url, state: returnPath ? { return: returnPath } : undefined }));
    };

    if (isDesktop && !isSmallDesktop) {
        return (
            <>
                <MonthlyReportsDrawer embeddedInV3Shell={embeddedInV3Shell} />
                <Box flex justify="between" gap={24} align="center">
                    {activeEnvironment.permissions.fundSubscribe && (
                        <Button
                            text={i18n.get("fund.operation.subscribe")}
                            variant="solid"
                            icon="CashflowOut"
                            className="w-100 px-5"
                            onClick={() => handleSubscribe("/fundcorpOperations/subscribe")}
                        />
                    )}
                    {activeEnvironment.permissions.fundRescue && hasPositionsToday && (
                        <Button
                            text={i18n.get("fund.operation.rescue")}
                            variant="solid"
                            icon="CashflowIn"
                            className="w-100 px-5"
                            onClick={() => handleRescue("/fundcorpOperations/rescue")}
                        />
                    )}
                    {showMonthlyReports ? (
                        <Button
                            text={i18n.get("fund.operation.fundExtracts")}
                            variant="outline"
                            icon="Memo"
                            iconVariant="outline"
                            className="w-100 px-5"
                            isInactive={shareAccounts?.length <= 0}
                            onClick={() => dispatch(fundcorpActions.setReportsDrawerVisible(true))}
                        />
                    ) : (
                        !hideDownload && (
                            <DownloadButton
                                isFetchingExport={isFetchingDownload || isFetchingFundcorp}
                                handleClick={handleExport}
                                handleClickMessage="global.download"
                                csvDownload
                                xlsDownload
                                imageStyle="mr-2"
                            />
                        )
                    )}

                    {itemActiveMultiple.length > 0 && <ContextMenu color="neutral" menuOptions={itemActiveMultiple} />}
                </Box>
            </>
        );
    }

    if (!isDesktop && !hideDownload) {
        itemActiveMultiple = [
            {
                icon: "SendDown",
                iconVariant: "outline",
                label: i18n.get("global.download"),
                callback: () => handleExport("pdf"),
            },
            ...itemActiveMultiple,
        ];
    }

    if ((!isDesktop || isSmallDesktop) && shareAccounts?.length) {
        itemActiveMultiple = [
            {
                icon: "Memo",
                iconVariant: "outline",
                label: i18n.get("fund.operation.fundExtracts"),
                callback: () => dispatch(fundcorpActions.setReportsDrawerVisible(true)),
            },
            ...itemActiveMultiple,
        ];
    }

    if (activeEnvironment.permissions.fundRescue && hasPositionsToday) {
        itemActiveMultiple = [
            {
                icon: "RescueDolar",
                iconVariant: "outline",
                label: i18n.get("fund.operation.rescue"),
                callback: () => handleRescue("/fundcorpOperations/rescue"),
            },
            ...itemActiveMultiple,
        ];
    }

    if (activeEnvironment.permissions.fundSubscribe) {
        itemActiveMultiple = [
            {
                icon: "StocksUp",
                iconVariant: "outline",
                label: i18n.get("fund.operation.subscribe"),
                callback: () => handleSubscribe("/fundcorpOperations/subscribe"),
            },
            ...itemActiveMultiple,
        ];
    }

    return (
        <>
            <MonthlyReportsDrawer embeddedInV3Shell={embeddedInV3Shell} />
            <ContextMenu color={isSmallDesktop ? "neutral" : "white"} menuOptions={itemActiveMultiple} />
        </>
    );
}

FundcorpRightContent.propTypes = {
    activeEnvironment: PropTypes.shape({
        permissions: PropTypes.shape({
            fundRescue: PropTypes.bool,
            fundSubscribe: PropTypes.bool,
        }),
    }).isRequired,
    isDesktop: PropTypes.bool.isRequired,
    isSmallDesktop: PropTypes.bool.isRequired,
    isFetchingFundcorp: PropTypes.bool.isRequired,
    isFetchingDownload: PropTypes.bool.isRequired,
    hasPositionsToday: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    handleExport: PropTypes.func.isRequired,
    shareAccounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    embeddedInV3Shell: PropTypes.bool,
    hideDownload: PropTypes.bool,
    location: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
    }),
};

FundcorpRightContent.defaultProps = {
    embeddedInV3Shell: false,
    location: undefined,
    hideDownload: false,
};

export default compose(withRouter, connect())(FundcorpRightContent);
